/** Admin dashboard — review professional applications. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, ExternalLink, ChevronLeft } from "lucide-react";
import { apiClient, API } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

const STATUSES = ["pending", "verified", "rejected", "all"];

export default function AdminDashboard() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState(null);
  const [rejecting, setRejecting] = useState(null); // application_id
  const [reason, setReason] = useState("");

  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const load = async () => {
    setRows(null);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await apiClient.get("/admin/professionals", { params });
      setRows(data);
    } catch (e) {
      setRows([]);
      toast.error(e.response?.data?.detail || "Could not load applications");
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [filter, isAdmin]);

  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "#F4F3F1" }}>
        <div className="text-center">
          <div className="font-barlow-cond" style={{ fontSize: 28, fontWeight: 800 }}>Sign in to continue</div>
          <button
            onClick={() => navigate("/login")}
            className="mt-4 px-6 h-11 rounded-pill"
            style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 600 }}
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen grid place-items-center px-6" style={{ background: "#F4F3F1" }}>
        <div className="text-center max-w-sm">
          <div className="font-barlow-cond" style={{ fontSize: 32, fontWeight: 900 }}>Access restricted</div>
          <p className="font-barlow mt-2" style={{ color: "#6B6760" }}>
            This dashboard is for FitFrik administrators only.
          </p>
          <p className="font-barlow mt-4 text-[13px]" style={{ color: "#8E8A84" }}>
            Hint: sign in as <code>admin@fitfrik.in</code> / <code>Admin@1234</code>
          </p>
        </div>
      </div>
    );
  }

  const verify = async (id) => {
    try {
      await apiClient.post(`/admin/professionals/${id}/verify`);
      toast.success("Application verified");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verify failed");
    }
  };

  const submitReject = async () => {
    if (reason.trim().length < 10) {
      toast.error("Reason must be at least 10 characters");
      return;
    }
    try {
      await apiClient.post(`/admin/professionals/${rejecting}/reject`, { reason });
      toast.success("Application rejected");
      setRejecting(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F4F3F1" }} data-testid="admin-dashboard">
      <header
        className="sticky top-0 z-10"
        style={{ background: "#F4F3F1", borderBottom: "1px solid #E2E0DC" }}
      >
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              aria-label="Back"
              data-testid="admin-back"
              className="-ml-2 w-10 h-10 grid place-items-center rounded-full hover:bg-white/60 transition-colors"
            >
              <ChevronLeft size={22} />
            </button>
            <div>
              <div className="font-barlow uppercase" style={{ fontSize: 11, fontWeight: 700, color: "#8E8A84", letterSpacing: "0.08em" }}>
                FitFrik Admin
              </div>
              <h1 className="font-barlow-cond" style={{ fontSize: 24, fontWeight: 900 }}>Professional Applications</h1>
            </div>
          </div>
          <div className="flex items-center gap-3 font-barlow" style={{ fontSize: 13, color: "#6B6760" }}>
            <button
              onClick={() => navigate("/admin/brand-applications")}
              data-testid="nav-admin-brand"
              className="px-3 h-9 rounded-pill border border-line2 text-[12px] font-semibold"
              style={{ borderRadius: 100 }}
            >
              Brand apps
            </button>
            <span>{user.email}</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6">
        {/* Filter */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1" data-testid="filter-row">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className="font-barlow capitalize"
              style={{
                background: filter === s ? "#00D4FF" : "transparent",
                color: filter === s ? "#F4F3F1" : "#6B6760",
                border: filter === s ? "1px solid #00D4FF" : "1px solid #C7C4BF",
                fontSize: 13, fontWeight: filter === s ? 700 : 600,
                height: 36, borderRadius: 100, padding: "0 16px",
              }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        {rows === null ? (
          <div className="font-barlow text-center py-12" style={{ color: "#8E8A84" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 font-barlow" style={{ color: "#8E8A84" }}>
            No {filter !== "all" ? filter : ""} applications
          </div>
        ) : (
          <>
            {/* MOBILE — stacked cards */}
            <div className="md:hidden space-y-3">
              {rows.map((r) => (
                <ApplicationCard
                  key={r.application_id}
                  r={r}
                  onVerify={verify}
                  onReject={(id) => { setRejecting(id); setReason(""); }}
                />
              ))}
            </div>

            {/* DESKTOP — table */}
            <div
              className="hidden md:block overflow-hidden"
              style={{
                background: "#FFFFFF", border: "1px solid #E2E0DC",
                borderRadius: 16,
              }}
            >
              <div
                className="grid font-barlow uppercase"
                style={{
                  gridTemplateColumns: "1.4fr 1fr 1fr 1.5fr 1fr 1fr 1.4fr",
                  fontSize: 11, fontWeight: 700, color: "#8E8A84",
                  letterSpacing: "0.08em",
                  padding: "14px 16px",
                  borderBottom: "1px solid #E2E0DC",
                }}
              >
                <div>Name</div>
                <div>Role</div>
                <div>City</div>
                <div>Credential</div>
                <div>Submitted</div>
                <div>Status</div>
                <div className="text-right">Actions</div>
              </div>

              {rows.map((r) => (
                <div
                  key={r.application_id}
                  className="grid items-center"
                  style={{
                    gridTemplateColumns: "1.4fr 1fr 1fr 1.5fr 1fr 1fr 1.4fr",
                    padding: "14px 16px",
                    borderBottom: "1px solid #EEECE9",
                  }}
                  data-testid={`app-row-${r.application_id}`}
                >
                  <div className="font-barlow" style={{ fontSize: 14, fontWeight: 600, color: "#1A1A1A" }}>
                    {r.full_name}
                  </div>
                  <div className="font-barlow capitalize" style={{ fontSize: 13, color: "#6B6760" }}>
                    {r.role}
                  </div>
                  <div className="font-barlow" style={{ fontSize: 13, color: "#6B6760" }}>
                    {r.city}
                  </div>
                  <div className="font-barlow" style={{ fontSize: 13, color: "#6B6760" }}>
                    {r.role === "trainer" ? r.certification_body : r.qualification}
                    {r.certificate_file_id && (
                      <a
                        href={`${API}/professionals/files/${r.certificate_file_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 ml-2"
                        style={{ color: "#00B0D5" }}
                        data-testid={`view-cert-${r.application_id}`}
                      >
                        <ExternalLink size={11} /> View
                      </a>
                    )}
                  </div>
                  <div className="font-barlow" style={{ fontSize: 12, color: "#8E8A84" }}>
                    {(r.submitted_at || "").slice(0, 10)}
                  </div>
                  <div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="flex justify-end gap-2">
                    {r.status === "pending" && (
                      <>
                        <button
                          onClick={() => verify(r.application_id)}
                          data-testid={`verify-${r.application_id}`}
                          className="font-barlow flex items-center gap-1"
                          style={{
                            background: "#00D4FF", color: "#000",
                            height: 32, borderRadius: 100, padding: "0 12px",
                            fontSize: 12, fontWeight: 700,
                          }}
                        >
                          <Check size={14} /> Verify
                        </button>
                        <button
                          onClick={() => { setRejecting(r.application_id); setReason(""); }}
                          data-testid={`reject-${r.application_id}`}
                          className="font-barlow flex items-center gap-1"
                          style={{
                            background: "transparent", color: "#E5484D",
                            border: "1px solid #E5484D",
                            height: 32, borderRadius: 100, padding: "0 12px",
                            fontSize: 12, fontWeight: 600,
                          }}
                        >
                          <X size={14} /> Reject
                        </button>
                      </>
                    )}
                    {r.status === "rejected" && (
                      <button
                        onClick={() => verify(r.application_id)}
                        data-testid={`verify-${r.application_id}`}
                        className="font-barlow"
                        style={{
                          background: "transparent", color: "#00B0D5",
                          border: "1px solid #C7C4BF",
                          height: 32, borderRadius: 100, padding: "0 12px",
                          fontSize: 12, fontWeight: 600,
                        }}
                      >
                        Override → Verify
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Reject modal */}
      {rejecting && (
        <div
          className="fixed inset-0 z-[60] grid place-items-center px-5"
          style={{ background: "rgba(0,0,0,0.45)" }}
          data-testid="reject-modal"
        >
          <div
            className="w-full max-w-md p-6"
            style={{ background: "#FFFFFF", border: "1px solid #E2E0DC", borderRadius: 16 }}
          >
            <h3 className="font-barlow-cond" style={{ fontSize: 24, fontWeight: 800 }}>
              Reject application
            </h3>
            <p className="font-barlow mt-1" style={{ fontSize: 13, color: "#6B6760" }}>
              The applicant will see this reason. Be specific and actionable.
            </p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="reject-reason-input"
              placeholder="e.g. Certificate scan is unreadable. Please re-upload a clearer photo."
              className="w-full font-barlow outline-none mt-4"
              style={{
                background: "#ECEAE7", border: "1px solid #E2E0DC",
                borderRadius: 12, padding: 12,
                fontSize: 14, color: "#1A1A1A", minHeight: 110, resize: "vertical",
              }}
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => { setRejecting(null); setReason(""); }}
                className="flex-1 h-11 font-barlow"
                style={{ border: "1px solid #E2E0DC", borderRadius: 100, fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                onClick={submitReject}
                data-testid="confirm-reject"
                className="flex-1 h-11 font-barlow"
                style={{
                  background: "#E5484D", color: "#fff",
                  borderRadius: 100, fontWeight: 700,
                }}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ApplicationCard({ r, onVerify, onReject }) {
  return (
    <div
      data-testid={`app-row-${r.application_id}`}
      style={{
        background: "#FFFFFF",
        border: "1px solid #E2E0DC",
        borderRadius: 14,
        padding: 14,
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div
            className="font-barlow truncate"
            style={{ fontSize: 15, fontWeight: 700, color: "#1A1A1A" }}
          >
            {r.full_name}
          </div>
          <div
            className="font-barlow capitalize mt-0.5"
            style={{ fontSize: 13, color: "#6B6760" }}
          >
            {r.role} · {r.city}
          </div>
        </div>
        <StatusBadge status={r.status} />
      </div>

      <div className="flex items-center gap-2 mt-2.5 flex-wrap">
        <span
          className="font-barlow"
          style={{
            background: "#ECEAE7",
            border: "1px solid #E2E0DC",
            fontSize: 11,
            fontWeight: 600,
            color: "#6B6760",
            padding: "3px 9px",
            borderRadius: 100,
          }}
        >
          {r.role === "trainer" ? r.certification_body : r.qualification}
        </span>
        {r.certificate_file_id && (
          <a
            href={`${API}/professionals/files/${r.certificate_file_id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-barlow"
            style={{ fontSize: 12, color: "#00B0D5", fontWeight: 600 }}
            data-testid={`view-cert-${r.application_id}`}
          >
            <ExternalLink size={12} /> View certificate
          </a>
        )}
        <span
          className="font-barlow ml-auto"
          style={{ fontSize: 11, color: "#8E8A84" }}
        >
          {(r.submitted_at || "").slice(0, 10)}
        </span>
      </div>

      {(r.status === "pending" || r.status === "rejected") && (
        <div className="flex gap-2 mt-3">
          {r.status === "pending" && (
            <>
              <button
                onClick={() => onVerify(r.application_id)}
                data-testid={`verify-${r.application_id}`}
                className="font-barlow flex-1 flex items-center justify-center gap-1.5"
                style={{
                  background: "#00D4FF",
                  color: "#000",
                  height: 38,
                  borderRadius: 100,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                <Check size={15} /> Verify
              </button>
              <button
                onClick={() => onReject(r.application_id)}
                data-testid={`reject-${r.application_id}`}
                className="font-barlow flex-1 flex items-center justify-center gap-1.5"
                style={{
                  background: "transparent",
                  color: "#E5484D",
                  border: "1px solid #E5484D",
                  height: 38,
                  borderRadius: 100,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <X size={15} /> Reject
              </button>
            </>
          )}
          {r.status === "rejected" && (
            <button
              onClick={() => onVerify(r.application_id)}
              data-testid={`verify-${r.application_id}`}
              className="font-barlow w-full"
              style={{
                background: "transparent",
                color: "#00B0D5",
                border: "1px solid #C7C4BF",
                height: 38,
                borderRadius: 100,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Override → Verify
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function StatusBadge({ status }) {
  const cfg = {
    pending: { bg: "#FFF7E6", fg: "#A05E00" },
    verified: { bg: "rgba(0, 212, 255, 0.18)", fg: "#00708A" },
    rejected: { bg: "#FCE6E6", fg: "#A52121" },
  }[status] || { bg: "#ECEAE7", fg: "#6B6760" };
  return (
    <span
      className="font-barlow uppercase inline-block"
      style={{
        background: cfg.bg, color: cfg.fg,
        fontSize: 11, fontWeight: 700,
        padding: "4px 10px", borderRadius: 100, letterSpacing: "0.06em",
      }}
    >
      {status}
    </span>
  );
}
